import { BottomSheetModal, BottomSheetScrollViewMethods } from '@gorhom/bottom-sheet'
import { Box, Row, Text, Divider, Button, useTheme } from 'native-base'
import { RefObject, useCallback, useEffect, useReducer, useState } from 'react'
import { TextInput, StyleSheet, Platform } from 'react-native'

import { CheckBoxList } from '../components/CheckBoxList'
import { SelectList } from '../components/SelectList'
import { Spacer } from '../components/atoms/Spacer'
import { BottomSheet, BottomSheetScrollView } from './BottomSheet'

import { bottomSheetContentHeight } from '~constants'
import { CategoryType } from '~contexts'
import { filtersReducer, dispatchType } from '~filtersReducer'
import { useFilters, useRef, useSafeAreaInsets, useTranslation } from '~hooks'
import { useGetCategoriesList, useGetCountriesList } from '~query-hooks'
type Props = {
  filterBottomSheetRef: RefObject<BottomSheetModal>
}
export const FilterBottomSheet = ({ filterBottomSheetRef }: Props) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { bottom } = useSafeAreaInsets()
  const bottomSheetScrollViewRef = useRef<BottomSheetScrollViewMethods>(null)
  const isClosing = useRef<boolean>(false)
  const [isKeyboardShown, setIsKeyboardShown] = useState(false)
  const { filtersCategoryList } = useGetCategoriesList()
  const { countries: locationList } = useGetCountriesList()
  const closeBottomSheet = useCallback(() => {
    setTimeout(() => {
      filterBottomSheetRef.current?.snapToPosition(-1)
      isClosing.current = false
    }, 0)
  }, [filterBottomSheetRef])

  const { filtersState, filtersDispatch, offersStatusList } = useFilters()

  const [state, dispatch] = useReducer(filtersReducer, filtersState)
  const { categoriesList, coinRange, selectedLocation, selectedOfferStatus } = state
  const {
    RESET_FILTERS,
    SET_INITIAL_STATE,
    UPDATE_CATEGORIES_LIST,
    UPDATE_COIN_RANGE,
    UPDATE_SELECTED_LOCATION,
    UPDATE_SELECTED_OFFER_STATUS,
  } = dispatchType
  useEffect(() => {
    dispatch({ type: SET_INITIAL_STATE, payload: filtersState })
  }, [SET_INITIAL_STATE, filtersState])

  useEffect(() => {
    const checkBoxesCategories =
      filtersCategoryList?.map((category) => ({
        ...category,
        isChecked: false,
      })) || []

    dispatch({ type: UPDATE_CATEGORIES_LIST, payload: checkBoxesCategories })
  }, [UPDATE_CATEGORIES_LIST, filtersCategoryList])

  const selectCheckBoxHandler = useCallback(
    (value: string) => {
      const selectProperCheckBox = (element: CategoryType): CategoryType => {
        if (element.value === value) {
          return { ...element, isChecked: !element.isChecked }
        }
        return element
      }
      const selectedList: CategoryType[] = categoriesList.map((checkBox) =>
        selectProperCheckBox(checkBox)
      )
      dispatch({ type: UPDATE_CATEGORIES_LIST, payload: selectedList })
    },
    [UPDATE_CATEGORIES_LIST, categoriesList]
  )

  const setCoinRange = useCallback(
    (text: string, type: 'from' | 'to') => {
      dispatch({
        type: UPDATE_COIN_RANGE,
        payload: { ...coinRange, [type]: text },
      })
    },
    [UPDATE_COIN_RANGE, coinRange]
  )

  const keyboardShownTimeout = useRef<NodeJS.Timeout | null>(null)

  const setLocation = useCallback(
    (payload: typeof filtersState.selectedLocation) => {
      dispatch({ type: UPDATE_SELECTED_LOCATION, payload })
    },
    [UPDATE_SELECTED_LOCATION, filtersState]
  )

  const setOfferStatus = useCallback(
    (payload: typeof selectedOfferStatus) => {
      dispatch({ type: UPDATE_SELECTED_OFFER_STATUS, payload })
    },
    [UPDATE_SELECTED_OFFER_STATUS]
  )

  const clearFiltersHandler = useCallback(() => {
    filtersDispatch({ type: RESET_FILTERS })
    closeBottomSheet()
  }, [filtersDispatch, RESET_FILTERS, closeBottomSheet])

  const checkMinMaxRangeHandler = useCallback(() => {
    const fromValue = coinRange.from
    const toValue = coinRange.to

    if (parseInt(fromValue, 10) > parseInt(toValue, 10) && !Number.isNaN(parseInt(toValue, 10))) {
      dispatch({ type: UPDATE_COIN_RANGE, payload: { from: toValue, to: fromValue } })
      return { from: toValue, to: fromValue }
    }
    keyboardShownTimeout.current = setTimeout(() => {
      setIsKeyboardShown(false)
    }, 100)
    return { from: fromValue, to: toValue }
  }, [UPDATE_COIN_RANGE, coinRange.from, coinRange.to])

  const onInputFocus = useCallback(async () => {
    if (Platform.OS === 'web') return
    if (keyboardShownTimeout.current) {
      clearTimeout(keyboardShownTimeout.current)
    }
    setIsKeyboardShown(true)
    setTimeout(() => {
      bottomSheetScrollViewRef.current?.scrollToEnd?.()
    }, 300)
  }, [])

  const submitFiltersHandler = useCallback(() => {
    isClosing.current = true
    const changeCoinRange = checkMinMaxRangeHandler()

    filtersDispatch({
      type: UPDATE_COIN_RANGE,
      payload: changeCoinRange,
    })
    filtersDispatch({ type: UPDATE_CATEGORIES_LIST, payload: categoriesList })
    filtersDispatch({
      type: UPDATE_SELECTED_LOCATION,
      payload: selectedLocation,
    })
    filtersDispatch({
      type: UPDATE_SELECTED_OFFER_STATUS,
      payload: selectedOfferStatus,
    })

    closeBottomSheet()
  }, [
    UPDATE_CATEGORIES_LIST,
    UPDATE_COIN_RANGE,
    UPDATE_SELECTED_LOCATION,
    UPDATE_SELECTED_OFFER_STATUS,
    categoriesList,
    checkMinMaxRangeHandler,
    closeBottomSheet,
    filtersDispatch,
    selectedLocation,
    selectedOfferStatus,
  ])

  return (
    <BottomSheet ref={filterBottomSheetRef}>
      <Row px="4" justifyContent="space-between">
        <Text bold fontSize="xl">
          {t('offers_list_screen.filter')}
        </Text>
      </Row>
      <BottomSheetScrollView
        ref={bottomSheetScrollViewRef}
        style={{ maxHeight: bottomSheetContentHeight }}
      >
        <Box px="4" pt="6">
          <Text fontSize="md" bold>
            {t('offers_list_screen.categories')}
          </Text>
          <Spacer y="5" />
          <CheckBoxList onPress={selectCheckBoxHandler} checkBoxList={categoriesList} />
        </Box>
        <Divider />
        <Box px="4" width="full">
          <Spacer y="5" />
          <Text bold fontSize="md">
            {t('offers_list_screen.offers_status')}
          </Text>
          <Spacer y="4" />
          <SelectList
            selected={selectedOfferStatus.value}
            onChange={setOfferStatus}
            itemsList={offersStatusList}
          />
          <Spacer y="7" />
        </Box>
        <Divider />
        <Box px="4" width="full">
          <Spacer y="5" />
          <Text bold fontSize="md">
            {t('offers_list_screen.location')}
          </Text>
          <Spacer y="4" />
          <SelectList
            selected={selectedLocation.value}
            onChange={setLocation}
            itemsList={locationList}
          />
          <Spacer y="7" />
        </Box>
        <Divider />
        <Box px="4" width="full">
          <Spacer y="5" />
          <Text bold fontSize="md">
            {t('offers_list_screen.coin_range')}
          </Text>
          <Spacer y="4" />
          <Row alignItems="center" mb={4} flex={1}>
            <Box flex={0.8}>
              <TextInput
                onFocus={onInputFocus}
                style={[styles.minMaxInput, { borderColor: colors['muted'][300] }]}
                placeholder={t('offers_list_screen.from')}
                keyboardType="number-pad"
                value={coinRange.from}
                // eslint-disable-next-line react/jsx-no-bind
                onChangeText={(text) => setCoinRange(text, 'from')}
                onBlur={checkMinMaxRangeHandler}
              />
            </Box>
            <Box mx={4} bg="text.400" width={3} height={0.5} />
            <Box flex={0.8}>
              <TextInput
                onFocus={onInputFocus}
                style={[styles.minMaxInput, { borderColor: colors['muted'][300] }]}
                placeholder={t('offers_list_screen.to')}
                keyboardType="number-pad"
                // eslint-disable-next-line react/jsx-no-bind
                onChangeText={(text) => setCoinRange(text, 'to')}
                onBlur={checkMinMaxRangeHandler}
                value={coinRange.to}
              />
            </Box>
          </Row>
          <Spacer y="7" />
        </Box>
        {isKeyboardShown ? <Spacer y="48" /> : null}
      </BottomSheetScrollView>
      <Divider />
      <Row pt="4" px="4" pb={bottom ? '4' : 0}>
        <Button flex={1} onPress={clearFiltersHandler} variant="ghost">
          <Text bold color="actionBlue" fontSize="md">
            {t('offers_list_screen.clear')}
          </Text>
        </Button>
        <Spacer x="2" />
        <Button bg="actionBlue" flex={2} onPress={submitFiltersHandler}>
          <Text bold color="white" fontSize="md">
            {t('offers_list_screen.filters_offers')}
          </Text>
        </Button>
      </Row>
    </BottomSheet>
  )
}
const styles = StyleSheet.create({
  // eslint-disable-next-line react-native/no-color-literals
  minMaxInput: {
    borderRadius: 5,
    borderWidth: 1,
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
})
